import React, { useRef, useState } from 'react';
import { Trash2, Upload } from 'lucide-react';
import { TaskyButton, TaskyFormGroup } from '../tasky';
import adminVideoTemplateService from '../../services/api/adminVideoTemplateService';

export interface VideoTemplateSampleValue {
  videoKey: string | null;
  posterKey: string | null;
  videoUrl: string | null;
  posterUrl: string | null;
}

interface VideoTemplateSampleFieldProps {
  value: VideoTemplateSampleValue;
  onChange: (value: VideoTemplateSampleValue) => void;
  onUploadingChange?: (uploading: boolean) => void;
  disabled?: boolean;
  /** テスト用の差し替え口（jsdom は動画をデコードできない） */
  capture?: (file: File) => Promise<Blob | null>;
}

const MAX_VIDEO_BYTES = 50 * 1024 * 1024;
const ACCEPTED_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];

/**
 * 動画の先頭付近を 1 枚切り出して JPEG にする。
 * 利用者の一覧ではこの画像だけを先に読み、動画はホバー時まで読まない。
 * 切り出せないブラウザ・形式ならポスター無しで続ける（一覧側は先頭フレームを読みにいく）。
 */
export const capturePoster = (file: File): Promise<Blob | null> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    let settled = false;
    const finish = (blob: Blob | null) => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(url);
      resolve(blob);
    };
    const timer = window.setTimeout(() => finish(null), 10000);
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.onloadedmetadata = () => {
      video.currentTime = Math.min(0.5, (video.duration || 1) / 2);
    };
    video.onseeked = () => {
      try {
        const scale = Math.min(1, 640 / (video.videoWidth || 640));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round((video.videoWidth || 640) * scale);
        canvas.height = Math.round((video.videoHeight || 400) * scale);
        canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          window.clearTimeout(timer);
          finish(blob);
        }, 'image/jpeg', 0.82);
      } catch {
        window.clearTimeout(timer);
        finish(null);
      }
    };
    video.onerror = () => {
      window.clearTimeout(timer);
      finish(null);
    };
    video.src = url;
  });

const VideoTemplateSampleField: React.FC<VideoTemplateSampleFieldProps> = ({
  value,
  onChange,
  onUploadingChange,
  disabled = false,
  capture = capturePoster,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setBusy = (busy: boolean) => {
    setUploading(busy);
    onUploadingChange?.(busy);
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('MP4 / WebM / MOV の動画を選んでください');
      return;
    }
    if (file.size > MAX_VIDEO_BYTES) {
      setError('動画は50MB以内にしてください');
      return;
    }

    setError(null);
    setBusy(true);
    try {
      const video = await adminVideoTemplateService.uploadSample('video', file, file.name);
      let poster: { key: string; url: string } | null = null;
      const posterBlob = await capture(file);
      if (posterBlob) {
        try {
          poster = await adminVideoTemplateService.uploadSample('poster', posterBlob, 'poster.jpg');
        } catch {
          poster = null;
        }
      }
      onChange({
        videoKey: video.key,
        videoUrl: video.url,
        posterKey: poster?.key ?? null,
        posterUrl: poster?.url ?? null,
      });
    } catch {
      setError('アップロードに失敗しました。時間をおいて再度お試しください');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = () => {
    onChange({ videoKey: null, posterKey: null, videoUrl: null, posterUrl: null });
  };

  return (
    <TaskyFormGroup
      label="サンプル動画"
      htmlFor="vt-sample_video"
      error={error || undefined}
      helper="登録すると、利用者の「動画スタイルを選ぶ」一覧のカードに表示されます（MP4 / WebM / MOV、50MBまで）"
    >
      <input
        ref={inputRef}
        id="vt-sample_video"
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        className="hidden"
        onChange={handleFile}
        disabled={disabled || uploading}
      />
      {value.videoUrl ? (
        <div className="space-y-2">
          <video
            src={value.videoUrl}
            poster={value.posterUrl || undefined}
            controls
            muted
            playsInline
            preload="metadata"
            className="aspect-[16/10] w-full max-w-sm rounded-lg bg-black object-cover"
            data-testid="vt-sample-preview"
          />
          <div className="flex gap-2">
            <TaskyButton
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={disabled || uploading}
            >
              <Upload className="h-4 w-4" />
              {uploading ? 'アップロード中…' : '差し替える'}
            </TaskyButton>
            <TaskyButton
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleRemove}
              disabled={disabled || uploading}
            >
              <Trash2 className="h-4 w-4" />
              外す
            </TaskyButton>
          </div>
        </div>
      ) : (
        <TaskyButton
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || uploading}
        >
          <Upload className="h-4 w-4" />
          {uploading ? 'アップロード中…' : '動画を選ぶ'}
        </TaskyButton>
      )}
    </TaskyFormGroup>
  );
};

export default VideoTemplateSampleField;
